import { useState, useRef, useEffect } from "react";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { getFormPayload } from "../../utils";
import { validatePhoneNumber } from "common/utils/validators/phone";
import { useDtfStore } from "../../store";
import { cn } from "tailwind-variants";
import "./DesignServicesSubscribe.scss";

export default function DesignServicesSubscribe({ ...restProps }) {
  const store = useDtfStore();
  const [subscribePhoneError, setSubscribePhoneError] = useState("");
  const [subscribePhone, setSubscribePhone] = useState(store.smsTransactionalPhone);
  const [subscribeButtonText, setSubscribeButtonText] = useState(
    store.smsTransactionalSubscribeButtonText
  );
  const subscribePhoneValidationTimeoutRef = useRef(undefined);

  useEffect(() => {
    store.setSmsTransactionalPhone(subscribePhone);
  }, [subscribePhone]);

  useEffect(() => {
    store.setSmsTransactionalSubscribeButtonText(subscribeButtonText);
  }, [subscribeButtonText]);

  function showDesignServicesDeliveryBuffer() {
    return store.showDesignServicesDeliveryBuffer;
  }

  function isSmsTransactionalSubscribed() {
    return store.messagingConsents?.smsTransactional;
  }

  function isSmsTransactionalSubscriptionPdpEnabled() {
    return store.smsTransactionalSubscriptionPdpEnabled;
  }

  function formatPhoneNumber(input) {
    let cleanedInput = input.replace(/\D/g, "");
    // Strip leading '1' if we have 11 digits (US/Canada country code)
    if (cleanedInput.length === 11 && cleanedInput[0] === "1") {
      cleanedInput = cleanedInput.slice(1);
    }
    let formatted = "";
    if (cleanedInput.length <= 3) {
      formatted = cleanedInput;
    } else if (cleanedInput.length <= 6) {
      formatted = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3)}`;
    } else {
      formatted = `(${cleanedInput.slice(0, 3)}) ${cleanedInput.slice(3, 6)}-${cleanedInput.slice(6, 10)}`;
    }
    return formatted;
  }

  function calculateNewCursorPosition(formatted, adjustedOldDigits) {
    // If cursor was at the very beginning (0 digits before it), keep it there
    if (adjustedOldDigits === 0) {
      return 0;
    }
    let newCursorPosition = 0;
    let digitCount = 0;
    for (let i = 0; i < formatted.length; i += 1) {
      if (formatted[i]?.match(/\d/)) {
        digitCount += 1;
        if (digitCount === adjustedOldDigits) {
          newCursorPosition = i + 1;
          break;
        }
      }
    }
    // If we didn't find enough digits, place at end
    if (digitCount < adjustedOldDigits) {
      newCursorPosition = formatted.length;
    }
    return newCursorPosition;
  }

  function restorePreviousValue(target, previousValue, prevCursor) {
    target.value = previousValue;
    target.setSelectionRange(prevCursor, prevCursor);
    setSubscribePhone(previousValue);
  }

  function salvageDigits(target, allDigits) {
    const salvaged = allDigits[0] === "1" ? allDigits.slice(0, 11) : allDigits.slice(0, 10);
    const formatted = formatPhoneNumber(salvaged);
    target.value = formatted;
    target.setSelectionRange(formatted.length, formatted.length);
    // eslint-disable-next-line dot-notation
    target.dataset["formattedValue"] = formatted;
    // eslint-disable-next-line dot-notation
    target.dataset["cursorPosition"] = formatted.length.toString();
    setSubscribePhone(formatted);
  }

  function handleInvalidLength(target, previousValue, allDigits) {
    if (previousValue) {
      const prevCursor = parseInt(
        // eslint-disable-next-line dot-notation
        target.dataset["cursorPosition"] || previousValue.length.toString(),
        10
      );
      restorePreviousValue(target, previousValue, prevCursor);
    } else if (allDigits.length >= 11) {
      salvageDigits(target, allDigits);
    }
  }

  function handleEmptyValue(target) {
    setSubscribePhone("");
    // eslint-disable-next-line dot-notation
    target.dataset["formattedValue"] = "";
    // eslint-disable-next-line dot-notation
    target.dataset["cursorPosition"] = "0";
    if (subscribeButtonText !== "Subscribe") {
      setSubscribeButtonText("Subscribe");
    }
  }

  function applyFormattedValue(target, formatted, options) {
    const adjustedOldDigits =
      options.strippedLeading1 && options.oldDigitsBeforeCursor > 0
        ? options.oldDigitsBeforeCursor - 1
        : options.oldDigitsBeforeCursor;
    const newCursorPosition = calculateNewCursorPosition(formatted, adjustedOldDigits);
    target.value = formatted;
    target.setSelectionRange(newCursorPosition, newCursorPosition);
    // eslint-disable-next-line dot-notation
    target.dataset["formattedValue"] = formatted;
    // eslint-disable-next-line dot-notation
    target.dataset["cursorPosition"] = newCursorPosition.toString();
    setSubscribePhone(formatted);
    if (subscribeButtonText !== "Subscribe") {
      setSubscribeButtonText("Subscribe");
    }
  }

  function isSubscribePhoneValid() {
    return validatePhoneNumber(subscribePhone) && !subscribePhoneError;
  }

  function validateSubscribePhone(phone) {
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      setSubscribePhoneError("");
      return false;
    }
    if (!validatePhoneNumber(trimmedPhone)) {
      setSubscribePhoneError("Please enter a valid 10-digit phone number");
      return false;
    }
    setSubscribePhoneError("");
    return true;
  }

  function getConsentParams() {
    return {
      spree_user: {
        preferences: {
          consent: {
            sms_transactional: true
          }
        }
      }
    };
  }

  async function handleSubscribeClick() {
    const isValid = validateSubscribePhone(subscribePhone);
    if (!isValid) return;
    const formPayload = getFormPayload();
    const orderNumber = formPayload.order_number;
    if (!orderNumber) {
      setSubscribePhoneError("Unable to subscribe right now. Please refresh the page.");
      return;
    }
    const csrfMetaTag = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfMetaTag ? csrfMetaTag.content : "";
    try {
      const response = await fetch(`/api/orders/${orderNumber}/update_redraw_phone`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken
            ? {
                "X-CSRF-Token": csrfToken
              }
            : {})
        },
        body: JSON.stringify({
          phone: subscribePhone,
          force_phone_update: true,
          consent_source: "transfer-pdp",
          ...getConsentParams()
        })
      });
      const data = await response.json();
      if (response.ok && data && data.success) {
        setSubscribePhoneError("");
        setSubscribeButtonText("Submitted");
      } else if (data && data.error) {
        setSubscribePhoneError(data.error);
      } else {
        setSubscribePhoneError("Failed to save phone number");
      }
    } catch (error) {
      setSubscribePhoneError("An error occurred. Please try again.");
    }
  }

  function handleSubscribeInput(event) {
    setSubscribePhoneError("");
    const { target } = event;
    const cursorPosition = target.selectionStart || 0;
    const currentValue = target.value;
    const allDigits = currentValue.replace(/\D/g, "");
    const isValidLength =
      allDigits.length <= 10 || (allDigits.length === 11 && allDigits[0] === "1");
    // eslint-disable-next-line dot-notation
    const previousValue = target.dataset["formattedValue"] || "";
    if (!isValidLength) {
      handleInvalidLength(target, previousValue, allDigits);
      return;
    }
    if (!currentValue) {
      handleEmptyValue(target);
      return;
    }
    const oldDigitsBeforeCursor = currentValue.slice(0, cursorPosition).replace(/\D/g, "").length;
    const formatted = formatPhoneNumber(currentValue);
    const strippedLeading1 = allDigits.length === 11 && allDigits[0] === "1";
    applyFormattedValue(target, formatted, {
      oldDigitsBeforeCursor,
      strippedLeading1
    });
    if (subscribePhoneValidationTimeoutRef.current !== undefined) {
      clearTimeout(subscribePhoneValidationTimeoutRef.current);
    }
    subscribePhoneValidationTimeoutRef.current = window.setTimeout(() => {
      validateSubscribePhone(subscribePhone);
    }, 1000);
  }

  return (
    <>
      {!isSmsTransactionalSubscribed() &&
      isSmsTransactionalSubscriptionPdpEnabled() &&
      !store.allArtworksAiApproved ? (
        <div
          {...restProps}
          className={cn("design-services__subscribe scope-c46a7105", restProps.className)}
        >
          <div className="design-services__subscribe-label scope-c46a7105">
            {" "}
            Get notified when your edits are ready{" "}
          </div>
          <div className="design-services__subscribe-fields scope-c46a7105">
            <input
              className="design-services__subscribe-input scope-c46a7105"
              value={subscribePhone}
              type="tel"
              placeholder="(xxx) xxx-xxxx"
              data-testid="design-services-subscribe-phone-input"
              disabled={subscribeButtonText === "Submitted"}
              onInput={e => {
                handleSubscribeInput(e);
              }}
            />

            <button
              className={cn("design-services__subscribe-button scope-c46a7105", {
                "design-services__subscribe-button--submitted": subscribeButtonText === "Submitted"
              })}
              type="button"
              data-testid="design-services-subscribe-button"
              disabled={!isSubscribePhoneValid()}
              onClick={handleSubscribeClick}
            >
              <span className="design-services__subscribe-button-text scope-c46a7105">
                {subscribeButtonText}{" "}
                {subscribeButtonText !== "Submitted" ? (
                  <span className="design-services__subscribe-button-asterisk scope-c46a7105">
                    {" "}
                    *{" "}
                  </span>
                ) : null}
              </span>
            </button>
          </div>
          {subscribePhoneError ? (
            <div
              className="design-services__subscribe-error scope-c46a7105"
              data-testid="design-services-subscribe-error"
            >
              {subscribePhoneError}
            </div>
          ) : null}
          <div
            className={cn("design-services__subscribe-note scope-c46a7105", {
              "design-services__subscribe-note--submitted": subscribeButtonText === "Submitted"
            })}
          >
            {subscribeButtonText === "Submitted" ? (
              <>
                <span className="design-services__subscribe-note-icon scope-c46a7105">
                  <UseSvg
                    className="design-services__subscribe-note-icon-svg scope-c46a7105"
                    icon="check-circle"
                    data-testid="design-services-subscribe-note-icon-svg"
                  />
                </span>
                <span className="design-services__subscribe-note-text scope-c46a7105">
                  {" "}
                  Be on the lookout for a SMS notification from Jiffy with your redrawn image{" "}
                </span>
              </>
            ) : (
              <>
                {showDesignServicesDeliveryBuffer() ? (
                  <>
                    {" "}
                    *Edits add 1 day to delivery. Enter your mobile number to receive transactional
                    SMS updates. Message and data rates may apply.{" "}
                  </>
                ) : (
                  <>
                    {" "}
                    *Enter your mobile number to receive transactional SMS updates. Message and data
                    rates may apply.{" "}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
